/**
 * Unified command menu (⌘K) — actions, navigation, and entity search with preview.
 *
 * Single entry point for everything: quick actions, page navigation,
 * full-text entity search, workspace switching, settings.
 * Preview panel slides open when a search result is selected.
 */

import { getSettingsNav } from "@/components/settings/SettingsSidebar";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { AccountingService } from "@/gen/omnios/v1/accounting_connect";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import { CommunicationService } from "@/gen/omnios/v1/communication_connect";
import { PartyService } from "@/gen/omnios/v1/party_connect";
import { PartyKind } from "@/gen/omnios/v1/party_pb";
import { transport } from "@/lib/grpc";
import { registerShortcut, getShortcut } from "@/lib/keyboard";
import { useOrganizationStore } from "@/lib/organization";
import { readRecentPages, rememberRecentPage } from "@/lib/recent-pages";
import { useWorkspaceStore } from "@/lib/workspace";
import { createClient } from "@connectrpc/connect";
import { useNavigate } from "@tanstack/react-router";
import {
	ArrowRightLeft,
	BookOpen,
	Bot,
	Building2,
	Calculator,
	ClipboardList,
	FileText,
	FolderTree,
	Globe,
	HeartPulse,
	Keyboard,
	ListTodo,
	MessageSquare,
	Navigation,
	Package,
	PenLine,
	Plus,
	Search,
	Settings,
	ShieldCheck,
	Smartphone,
	User,
	Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const client = createClient(CatalogService, transport);
const accountingClient = createClient(AccountingService, transport);
const partyClient = createClient(PartyService, transport);
const commClient = createClient(CommunicationService, transport);
const aiClient = createClient(AIService, transport);

const PARTY_KIND_KEYS: Record<number, string> = {
	[PartyKind.PERSON]: "party_kind_person",
	[PartyKind.ORGANIZATION]: "party_kind_organization",
	[PartyKind.TEAM]: "party_kind_team",
	[PartyKind.AGENT]: "party_kind_agent",
};

interface PageDef {
	labelKey: string;
	to: string;
	icon: typeof Package;
}

const PAGE_DEFS: PageDef[] = [
	{ labelKey: "nav:accounting", to: "/accounting", icon: BookOpen },
	{ labelKey: "accounting:nav_journal", to: "/accounting/journal", icon: BookOpen },
	{ labelKey: "accounting:nav_journal_new", to: "/accounting/journal/new", icon: BookOpen },
	{ labelKey: "accounting:nav_accounts", to: "/accounting/accounts", icon: ListTodo },
	{ labelKey: "accounting:nav_statements", to: "/accounting/statements", icon: FileText },
	{ labelKey: "accounting:nav_bank", to: "/accounting/bank", icon: Building2 },
	{ labelKey: "nav:products", to: "/catalog/products", icon: Package },
	{ labelKey: "nav:categories", to: "/catalog/categories", icon: FolderTree },
	{ labelKey: "nav:portals", to: "/portals", icon: Globe },
	{ labelKey: "nav:assistant", to: "/assistant", icon: Bot },
	{ labelKey: "nav:background_jobs", to: "/background-jobs", icon: ListTodo },
	{ labelKey: "cases", to: "/cases", icon: FileText },
	{ labelKey: "nav:settings", to: "/settings", icon: Settings },
];

function buildSettingsItems(t: (key: string) => string) {
	const SETTINGS_NAV = getSettingsNav(t);
	const items: { to: string; label: string; group: string; keywords: string }[] = [];
	for (const group of SETTINGS_NAV) {
		for (const item of group.items) {
			items.push({
				to: item.to,
				label: item.label,
				group: group.group,
				keywords: `${item.label} ${item.keywords ?? ""} ${group.group}`.toLowerCase(),
			});
			if (item.children) {
				for (const child of item.children) {
					if (child.to === item.to) continue;
					items.push({
						to: child.to,
						label: `${item.label} → ${child.label}`,
						group: group.group,
						keywords: `${item.label} ${child.label} ${child.keywords ?? ""} ${item.keywords ?? ""} ${group.group}`.toLowerCase(),
					});
				}
			}
		}
	}
	return items;
}

// ── Preview item type ──

interface PreviewItem {
	id: string;
	type: string;
	title: string;
	subtitle?: string;
	path: string;
	meta?: Record<string, string>;
}

export function CommandMenu() {
	const { t } = useTranslation();
	const tSettings = (key: string) => t(`settings:${key}`);
	const SETTINGS_ITEMS = buildSettingsItems(tSettings);
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [products, setProducts] = useState<{ id: string; title: string }[]>([]);
	const [categories, setCategories] = useState<{ id: string; name: string; path: string }[]>([]);
	const [parties, setParties] = useState<{ id: string; name: string; kind: PartyKind }[]>([]);
	const [conversations, setConversations] = useState<{ conversationId: string; text: string }[]>([]);
	const [knowledgeDocs, setKnowledgeDocs] = useState<{ id: string; title: string; chunkCount: number }[]>([]);
	const [accounts, setAccounts] = useState<{ code: string; name: string }[]>([]);
	const [hoveredItem, setHoveredItem] = useState<PreviewItem | null>(null);
	const navigate = useNavigate();
	const inputRef = useRef<HTMLInputElement>(null);
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	// Determine if we have entity results (to show preview panel)
	const hasEntityResults = products.length > 0 || parties.length > 0 || accounts.length > 0 || conversations.length > 0;

	useEffect(() => {
		return registerShortcut({
			id: "global.command-menu",
			keys: "meta+k",
			label: "shortcuts_search",
			displayKeys: "⌘K",
			category: "global",
			action: () => setOpen((prev) => !prev),
		});
	}, []);

	useEffect(() => {
		if (!open) {
			setQuery("");
			setProducts([]);
			setCategories([]);
			setParties([]);
			setConversations([]);
			setKnowledgeDocs([]);
			setAccounts([]);
			setHoveredItem(null);
		}
	}, [open]);

	const search = useCallback((q: string) => {
		setQuery(q);
		setHoveredItem(null);
		if (q.length < 2) {
			setProducts([]);
			setCategories([]);
			setParties([]);
			setConversations([]);
			setKnowledgeDocs([]);
			setAccounts([]);
			return;
		}
		if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		abortRef.current?.abort();

		searchTimerRef.current = setTimeout(async () => {
			const controller = new AbortController();
			abortRef.current = controller;
			await searchInner(q, controller.signal);
		}, 200);
	}, []);

	const searchInner = useCallback(async (q: string, signal: AbortSignal) => {
		try {
			const [prodRes, catRes, partyRes, msgRes, knowledgeRes, accountsRes] =
				await Promise.all([
					client.listProducts({ page: { pageSize: 6, pageToken: "" }, search: q }),
					client.listCategories({ parentId: "*" }),
					partyClient.listParties({ search: q, page: { pageSize: 6, pageToken: "" } }),
					commClient.searchMessages({ query: q, limit: 5 }),
					aiClient.listKnowledgeDocuments({ page: { pageSize: 50, pageToken: "" } }),
					accountingClient.listAccounts({}).catch(() => ({ accounts: [] })),
				]);

			if (signal.aborted) return;

			setProducts(prodRes.products.map((p) => ({ id: p.id, title: p.title })));
			const qLower = q.toLowerCase();
			setCategories(
				catRes.categories
					.filter((c) => c.name.toLowerCase().includes(qLower) || c.slug.toLowerCase().includes(qLower))
					.slice(0, 5)
					.map((c) => ({ id: c.id, name: c.name, path: c.path })),
			);
			setParties(partyRes.parties.map((p) => ({ id: p.id, name: p.name, kind: p.kind })));
			setConversations(msgRes.hits.map((h) => ({ conversationId: h.conversationId, text: h.text })));
			setKnowledgeDocs(
				(knowledgeRes.documents ?? [])
					.filter((d) => d.title.toLowerCase().includes(qLower) || d.filename.toLowerCase().includes(qLower))
					.slice(0, 5)
					.map((d) => ({ id: d.id, title: d.title || d.filename, chunkCount: d.chunkCount })),
			);
			setAccounts(
				(accountsRes.accounts ?? [])
					.filter((a) => a.code.toLowerCase().includes(qLower) || a.name.toLowerCase().includes(qLower))
					.slice(0, 6)
					.map((a) => ({ code: a.code, name: a.name })),
			);
		} catch {
			if (signal.aborted) return;
			setProducts([]);
			setCategories([]);
			setParties([]);
			setConversations([]);
			setKnowledgeDocs([]);
			setAccounts([]);
		}
	}, []);

	const go = (to: string, label?: string) => {
		setOpen(false);
		navigate({ to });
		rememberRecentPage(to, label);
	};

	const recentPages = useMemo(() => readRecentPages(5), [open]);

	const withPreview = useCallback(
		(item: PreviewItem) => ({
			onMouseEnter: () => setHoveredItem(item),
			onFocus: () => setHoveredItem(item),
		}),
		[],
	);

	// Shortcut display keys for actions
	const agentKeys = getShortcut("global.agent-toggle")?.displayKeys;
	const newKeys = getShortcut("global.new")?.displayKeys;
	const helpKeys = getShortcut("global.shortcuts-help")?.displayKeys;

	return (
		<>
			<Button
				type="button"
				variant="outline"
				size="sm"
				className="flex h-8 items-center gap-2 bg-muted/40 px-3 text-muted-foreground hover:bg-muted/60"
				onClick={() => setOpen(true)}
			>
				<Search className="h-3.5 w-3.5 shrink-0" />
				<span className="hidden sm:inline">{t("search")}</span>
				<kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium sm:flex">
					<span className="text-xs">⌘</span>K
				</kbd>
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent
					className={`overflow-hidden p-0 top-[18%] translate-y-0 data-[state=closed]:slide-out-to-top-[18%] data-[state=open]:slide-in-from-top-[18%] transition-all duration-200 ${
						hasEntityResults && hoveredItem ? "max-w-2xl" : "max-w-lg"
					}`}
				>
					<div className="flex">
						{/* Left: command list */}
						<div className={`flex flex-col ${hasEntityResults && hoveredItem ? "w-[380px]" : "flex-1"}`}>
							<Command
								shouldFilter={false}
								className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
							>
								<CommandInput
									ref={inputRef}
									placeholder={t("search_placeholder")}
									value={query}
									onValueChange={search}
								/>
								<CommandList className="max-h-[380px]">
									<CommandEmpty>
										{query.length < 2 && recentPages.length === 0
											? t("search_hint")
											: query.length >= 2 ? t("no_results") : null}
									</CommandEmpty>

									{/* Quick actions when empty */}
									{query.length < 2 && (
										<CommandGroup heading={t("actions")}>
											<CommandItem value="action-new" onSelect={() => { setOpen(false); document.dispatchEvent(new KeyboardEvent("keydown", { key: "n", metaKey: true })); }}>
												<Plus className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>{t("command_new_item")}</span>
												{newKeys && <kbd className="ml-auto h-5 rounded border bg-muted px-1.5 font-mono text-[10px]">{newKeys}</kbd>}
											</CommandItem>
											<CommandItem value="action-agent" onSelect={() => { setOpen(false); document.dispatchEvent(new KeyboardEvent("keydown", { key: "j", metaKey: true })); }}>
												<Bot className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>{t("assistant")}</span>
												{agentKeys && <kbd className="ml-auto h-5 rounded border bg-muted px-1.5 font-mono text-[10px]">{agentKeys}</kbd>}
											</CommandItem>
											<CommandItem value="action-help" onSelect={() => { setOpen(false); document.dispatchEvent(new KeyboardEvent("keydown", { key: "?", metaKey: true })); }}>
												<Keyboard className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>{t("keyboard_shortcuts")}</span>
												{helpKeys && <kbd className="ml-auto h-5 rounded border bg-muted px-1.5 font-mono text-[10px]">{helpKeys}</kbd>}
											</CommandItem>
										</CommandGroup>
									)}

									{query.length < 2 && (
										<CommandGroup heading="Создать">
											<CommandItem value="create-order" onSelect={() => go("/commerce/orders", "Создать заказ")}>
												<ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Создать заказ</span>
											</CommandItem>
											<CommandItem value="create-journal-entry" onSelect={() => go("/accounting/journal/new", "Создать проводку")}>
												<PenLine className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Создать проводку</span>
											</CommandItem>
											<CommandItem value="create-quote" onSelect={() => go("/commerce/quotes?create=1", "Создать КП")}>
												<FileText className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Создать КП</span>
											</CommandItem>
											<CommandItem value="create-work-item" onSelect={() => go("/work-items?create=1", "Создать обращение")}>
												<ListTodo className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Создать обращение</span>
											</CommandItem>
											<CommandItem value="create-diagnostics-policy" onSelect={() => go("/diagnostics/policies?create=1", "Создать политику записи")}>
												<ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Создать политику записи</span>
											</CommandItem>
											<CommandItem value="create-device-token" onSelect={() => go("/diagnostics/devices?create=1", "Создать токен устройства")}>
												<Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Создать токен устройства</span>
											</CommandItem>
										</CommandGroup>
									)}

									{query.length < 2 && (
										<CommandGroup heading="Перейти">
											<CommandItem value="nav-accounting" onSelect={() => go("/accounting", "Бухгалтерия")}>
												<BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Бухгалтерия</span>
											</CommandItem>
											<CommandItem value="nav-commerce" onSelect={() => go("/commerce", "Коммерция")}>
												<Navigation className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Коммерция</span>
											</CommandItem>
											<CommandItem value="nav-diagnostics" onSelect={() => go("/diagnostics", "Диагностика")}>
												<HeartPulse className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Диагностика</span>
											</CommandItem>
											<CommandItem value="nav-products" onSelect={() => go("/catalog/products", "Товары")}>
												<Package className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Товары</span>
											</CommandItem>
											<CommandItem value="nav-parties" onSelect={() => go("/parties", "Контакты")}>
												<Users className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Контакты</span>
											</CommandItem>
											<CommandItem value="nav-settings" onSelect={() => go("/settings", "Настройки")}>
												<Settings className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>Настройки</span>
											</CommandItem>
										</CommandGroup>
									)}

									{query.length < 2 && recentPages.length > 0 && (
										<CommandGroup heading={t("recent")}>
											{recentPages.map((r) => (
												<CommandItem key={r.to} value={`recent-${r.to}`} onSelect={() => go(r.to, r.label)}>
													<Search className="h-4 w-4 text-muted-foreground shrink-0" />
													<span className="truncate">{r.label}</span>
													<span className="ml-auto text-[10px] text-muted-foreground font-mono">{r.to}</span>
												</CommandItem>
											))}
										</CommandGroup>
									)}

									{products.length > 0 && (
										<CommandGroup heading={t("nav:products")}>
											{products.map((p) => (
												<CommandItem
													key={p.id}
													value={`product-${p.id}`}
													onSelect={() => go(`/catalog/products/${p.id}`)}
													{...withPreview({
														id: p.id,
														type: t("item_type_product"),
														title: p.title,
														path: `/catalog/products/${p.id}`,
													})}
												>
													<Package className="h-4 w-4 text-muted-foreground shrink-0" />
													<span className="truncate">{p.title}</span>
												</CommandItem>
											))}
										</CommandGroup>
									)}

									{parties.length > 0 && (
										<CommandGroup heading={t("nav:contacts")}>
											{parties.map((p) => {
												const Icon = p.kind === PartyKind.ORGANIZATION ? Building2 : User;
												const kindLabel = PARTY_KIND_KEYS[p.kind] ? t(PARTY_KIND_KEYS[p.kind]) : "";
												return (
														<CommandItem
															key={p.id}
															value={`party-${p.id}`}
															onSelect={() => go(`/parties/${p.id}`)}
															{...withPreview({
																id: p.id,
																type: t("item_type_contact"),
																title: p.name,
																subtitle: kindLabel,
																path: `/parties/${p.id}`,
															})}
														>
														<Icon className="h-4 w-4 text-muted-foreground shrink-0" />
														<span className="truncate">{p.name}</span>
														<span className="ml-auto text-xs text-muted-foreground">{kindLabel}</span>
													</CommandItem>
												);
											})}
										</CommandGroup>
									)}

									{accounts.length > 0 && (
										<CommandGroup heading={t("accounting:accounts")}>
											{accounts.map((a) => (
												<CommandItem
													key={a.code}
													value={`account-${a.code}-${a.name}`}
													onSelect={() => go(`/accounting/accounts/${a.code}`)}
													{...withPreview({
														id: a.code,
														type: t("item_type_account"),
														title: `${a.code} — ${a.name}`,
														path: `/accounting/accounts/${a.code}`,
													})}
												>
													<Calculator className="h-4 w-4 text-muted-foreground shrink-0" />
													<span className="font-mono text-xs mr-2">{a.code}</span>
													<span className="truncate">{a.name}</span>
												</CommandItem>
											))}
										</CommandGroup>
									)}

									{categories.length > 0 && (
										<CommandGroup heading={t("nav:categories")}>
											{categories.map((c) => (
												<CommandItem
													key={c.id}
													value={`cat-${c.id}`}
													onSelect={() => go("/catalog/categories", t("nav:categories"))}
													{...withPreview({
														id: c.id,
														type: t("nav:categories"),
														title: c.name,
														subtitle: c.path,
														path: "/catalog/categories",
													})}
												>
													<FolderTree className="h-4 w-4 text-amber-500 shrink-0" />
													<span className="truncate">{c.name}</span>
												</CommandItem>
											))}
										</CommandGroup>
									)}

									{conversations.length > 0 && (
										<CommandGroup heading={t("conversations")}>
											{conversations.map((c) => (
												<CommandItem
													key={c.conversationId}
													value={`conv-${c.conversationId}`}
													onSelect={() => go(`/assistant/${c.conversationId}`)}
													{...withPreview({
														id: c.conversationId,
														type: t("item_type_message"),
														title: c.text.slice(0, 80),
														path: `/assistant/${c.conversationId}`,
													})}
												>
													<MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
													<span className="truncate text-sm">{c.text.slice(0, 60)}</span>
												</CommandItem>
											))}
										</CommandGroup>
									)}

									{knowledgeDocs.length > 0 && (
										<CommandGroup heading={t("knowledge_base")}>
											{knowledgeDocs.map((d) => (
												<CommandItem
													key={d.id}
													value={`knowledge-${d.id}`}
													onSelect={() => go(`/knowledge-doc/${d.id}`, d.title)}
													{...withPreview({
														id: d.id,
														type: t("knowledge_base"),
														title: d.title,
														subtitle: t("fragments_count", { count: d.chunkCount }),
														path: `/knowledge-doc/${d.id}`,
													})}
												>
													<BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
													<span className="truncate">{d.title}</span>
												</CommandItem>
											))}
										</CommandGroup>
									)}

									<WorkspaceSwitchGroup query={query} onSwitch={() => setOpen(false)} />

									<CommandGroup heading={t("pages")}>
										{PAGE_DEFS.map((page) => (
											<CommandItem key={page.to} value={t(page.labelKey)} onSelect={() => go(page.to)}>
												<page.icon className="h-4 w-4 text-muted-foreground shrink-0" />
												<span>{t(page.labelKey)}</span>
											</CommandItem>
										))}
									</CommandGroup>

									{query.length >= 1 && (
										<CommandGroup heading={t("nav:settings")}>
											{SETTINGS_ITEMS.filter((s) => {
												const q = query.toLowerCase();
												return q.split(/\s+/).every((w) => s.keywords.includes(w));
											})
												.slice(0, 8)
												.map((s) => (
													<CommandItem key={s.to} value={`settings-${s.to}`} onSelect={() => go(s.to)}>
														<Settings className="h-4 w-4 text-muted-foreground shrink-0" />
														<span className="truncate">{s.label}</span>
														<span className="ml-auto text-xs text-muted-foreground">{s.group}</span>
													</CommandItem>
												))}
										</CommandGroup>
									)}
								</CommandList>
							</Command>
						</div>

						{/* Right: preview panel (visible when hovering entity results) */}
						{hasEntityResults && hoveredItem && (
							<>
								<Separator orientation="vertical" />
								<div className="flex-1 p-4 min-w-[240px]">
									<div className="flex items-center gap-2 mb-3">
										<span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
											{hoveredItem.type}
										</span>
									</div>
									<h3 className="text-base font-semibold mb-1">{hoveredItem.title}</h3>
									{hoveredItem.subtitle && (
										<p className="text-sm text-muted-foreground mb-3">{hoveredItem.subtitle}</p>
									)}
									<Separator className="my-3" />
									<div className="space-y-2">
										<div>
											<span className="text-[10px] text-muted-foreground">ID</span>
											<p className="text-xs font-mono">{hoveredItem.id}</p>
										</div>
										<div>
											<span className="text-[10px] text-muted-foreground">
												{t("path")}
											</span>
											<p className="text-xs font-mono">{hoveredItem.path}</p>
										</div>
									</div>
									<Button
										type="button"
										size="sm"
										className="mt-4"
										onClick={() => go(hoveredItem.path)}
									>
										{t("open")}
									</Button>
								</div>
							</>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}

function WorkspaceSwitchGroup({ query, onSwitch }: { query: string; onSwitch: () => void }) {
	const { t } = useTranslation("common");
	const workspaces = useWorkspaceStore((s) => s.workspaces);
	const selectedId = useWorkspaceStore((s) => s.selectedId);
	const setSelected = useWorkspaceStore((s) => s.setSelected);
	const setAllEntities = useOrganizationStore((s) => s.setAllEntities);

	if (query.length < 1) return null;

	const q = query.toLowerCase();
	const matches = workspaces
		.filter((w) => w.id !== selectedId && w.name.toLowerCase().includes(q))
		.slice(0, 5);

	if (matches.length === 0) return null;

	return (
		<CommandGroup heading={t("workspaces")}>
			{matches.map((ws) => (
				<CommandItem
					key={`ws-${ws.id}`}
					value={`switch-${ws.name}`}
					onSelect={() => { setAllEntities(false); setSelected(ws.id); onSwitch(); }}
				>
					<ArrowRightLeft className="h-4 w-4 text-muted-foreground shrink-0" />
					<span className="truncate">{ws.name}</span>
				</CommandItem>
			))}
		</CommandGroup>
	);
}
